import MidiEffect from './MidiEffect.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import MidiTrigger from './MidiTrigger.js';

class ParameterEffect extends MidiEffect {
    constructor(track) {
        super('Parameterizer', track);

        this.parameter = undefined;

        // parameters for the arpeggiator
        this.factorParameter = new AbstractParameter(
            'factor', this, (o, x) => x,
            {
                documentation: "Multiplies the current value of the parameter by this number",
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 1
            });
        this.registerParameter(this.factorParameter);

        // parameters for the arpeggiator
        this.offsetParameter = new AbstractParameter(
            'offset', this, (o, x) => x,
            {
                documentation: "Adds the current value of the parameter with this number",
                min: -100,
                max: 100,
                unit: '',
                defaultValue: 0
            });
        this.registerParameter(this.offsetParameter);

        // parameters for the arpeggiator
        this.probabilityParameter = new AbstractParameter(
            'probability', this, (o, x) => x,
            {
                documentation: "The probability that a parameter change will occur on a given trigger.",
                min: 0,
                max: 100,
                unit: '',
                defaultValue: 100
            });
        this.registerParameter(this.probabilityParameter);
    }

    get documentation() {
        return [
            "Alters the step parameters for triggers that pass through this effect. "
        ];
    }

    getCopy(track, ignoreId=true) {
        let fx = new ParameterEffect(track);
        fx.fromJson(this.getJson(), ignoreId);
        return fx;
    }

    getVisualizedParameters() {
        return [this.offsetParameter];
    }

    getText() {
        let offset = Math.round(100*this.offsetParameter.get(true))/100;
        if (offset > 0) {
            offset = "+" + offset;
        }
        if (this.parameter) {
            return this.parameter.name + ' ' + offset;
        }
        return offset;
    }

    getAvailableParameters() {
        return [
            this.track.trackData.durationParameter,
            this.track.trackData.chopParameter,
            this.track.trackData.velocityParameter,
            this.track.trackData.transposeParameter,
            this.track.trackData.paletteParameter,
            this.track.trackData.startParameter,
            this.track.trackData.attackParameter,
            this.track.trackData.releaseParameter,
            this.track.trackData.pitchAttackParameter,
            this.track.trackData.pitchDecayParameter,
            this.track.trackData.pitchSustainParameter,
            this.track.trackData.pitchReleaseParameter,
        ];
    }

    set factor(x) {
        this.factorParameter.set(x);
    }

    get factor() {
        return this.factorParameter.get();
    }

    set offset(x) {
        this.offsetParameter.set(x);
    }

    get offset() {
        return this.offsetParameter.get();
    }

    set probability(x) {
        this.probabilityParameter.set(x);
    }

    get probability() {
        return this.probabilityParameter.get();
    }

    output() {
        if (this.parameter === undefined) {
            return this.triggers;
        }


        let triggers = [];
        for (let i in this.triggers) {
            let trigger = this.triggers[i];

            let stepData = this.getStep(trigger.stepData, undefined, undefined, true); 
            // find the parameter
            let filtered = stepData.parameters
                .filter(x => x.name === this.parameter.name);

            if (filtered.length > 0) {
                let value = filtered[0].get();
                if (value === null) {
                    let trackParameter = this.track.trackData
                        .parameters.find(x => x.name === filtered[0].name);
                    value = trackParameter.get();
                }

                let random = Math.random();
                if (random < this.probability / 100.0) {
                    filtered[0].set(value * this.factor + this.offset);
                }
            } 
            trigger = new MidiTrigger(stepData, trigger.time, trigger.stepNumber);
            triggers.push(trigger);
        }

        return triggers;
    }

    getJson() {
        return {
            ...super.getJson(),
            parameter: this.parameter === undefined ? undefined : this.parameter.name,
        };
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);

        let parameters = this.getAvailableParameters();

        if (json.parameter === undefined) {
            return;
        }

        this.parameter = parameters.filter(
            p => p.name === json.parameter)[0];
        return bulk;
    }
}

export default ParameterEffect;
